/**
 * The combo bounded context.
 *
 * One score value is shared by three surfaces — compact Combo Lab, the wide
 * Combo Studio (Compose + Rehearse) and Ringside Spotlight. The Swift app got
 * this right by making beat identity, not array offset, the anchor for locks,
 * phrase brackets and response branches; every type here preserves that so a
 * remix of neighbouring beats cannot silently detach a bracket, a branch, an
 * accessibility announcement, or the Ringside spotlight.
 *
 * Nothing in this context detects punches, scores correctness, or counts
 * repetitions. Every advance is an explicit user or timer action, and no type
 * here may grow a field that implies otherwise.
 */

import type { Milliseconds } from "./time";
// Guided themes are a property of a planned round, so PLANNING owns the enum
// and persists its case names; the parameter recipe each theme expands into is
// combo knowledge and lives here. The edge runs COMBAT -> PLANNING only.
import type { GuidedComboTheme } from "./planning";

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------

declare const FF_COMBO_BRAND: unique symbol;

type Branded<Tag extends string> = string & { readonly [FF_COMBO_BRAND]: Tag };

/** A saved or generated score. Also the namespace for derived beat identity. */
export type ComboId = Branded<"ComboId">;

/**
 * The stable identity of a single beat. This is what survives reorder,
 * duplicate and remix, what accessibility announcements name, and what the
 * Ringside spotlight keys off — never the index.
 */
export type ComboBeatId = Branded<"ComboBeatId">;

export type PhraseBracketId = Branded<"PhraseBracketId">;
export type ResponseBranchId = Branded<"ResponseBranchId">;
export type ResponseOptionId = Branded<"ResponseOptionId">;

export function comboId(value: string): ComboId {
  return value as ComboId;
}

export function comboBeatId(value: string): ComboBeatId {
  return value as ComboBeatId;
}

export function phraseBracketId(value: string): PhraseBracketId {
  return value as PhraseBracketId;
}

export function responseBranchId(value: string): ResponseBranchId {
  return value as ResponseBranchId;
}

export function responseOptionId(value: string): ResponseOptionId {
  return value as ResponseOptionId;
}

/**
 * Monotonic counter stamped on a score every time an editing transaction is
 * applied. Rehearse and Ringside record the revision they rendered, which is
 * how "Compose, Rehearse and Ringside consume one score" becomes checkable
 * rather than merely intended.
 */
export type ComboScoreRevision = number & { readonly [FF_COMBO_BRAND]: "ComboScoreRevision" };

export function comboScoreRevision(value: number): ComboScoreRevision {
  return value as ComboScoreRevision;
}

// ---------------------------------------------------------------------------
// Vocabulary — punches
// ---------------------------------------------------------------------------

export type BoxingStance = "Orthodox" | "Southpaw";

export const BOXING_STANCES: readonly BoxingStance[] = ["Orthodox", "Southpaw"];

/**
 * The number is the persisted identity; the name is derived. Lead/rear comes
 * from parity, so switching stance never rewrites stored beats — the same score
 * reads correctly from either side.
 */
export type PunchNumber = 1 | 2 | 3 | 4 | 5 | 6;

export const PUNCH_NUMBERS: readonly PunchNumber[] = [1, 2, 3, 4, 5, 6];

export const PUNCH_NAMES: Readonly<Record<PunchNumber, string>> = {
  1: "Jab",
  2: "Cross",
  3: "Lead hook",
  4: "Rear hook",
  5: "Lead uppercut",
  6: "Rear uppercut",
};

export function punchName(punch: PunchNumber): string {
  return PUNCH_NAMES[punch];
}

export function isLeadPunch(punch: PunchNumber): boolean {
  return punch % 2 === 1;
}

/** Power is ordered: `touch < sharp < power`. */
export type PowerLevel = 1 | 2 | 3;

export const POWER_TOUCH: PowerLevel = 1;
export const POWER_SHARP: PowerLevel = 2;
export const POWER_FULL: PowerLevel = 3;

export const POWER_LEVELS: readonly PowerLevel[] = [1, 2, 3];

export const POWER_LABELS: Readonly<Record<PowerLevel, string>> = {
  1: "Touch",
  2: "Sharp",
  3: "Power",
};

export const POWER_SHORT_LABELS: Readonly<Record<PowerLevel, string>> = {
  1: "T",
  2: "S",
  3: "P",
};

/**
 * Power is never conveyed by colour or size alone; every node also carries this
 * spoken value.
 */
export function powerAccessibilityLabel(power: PowerLevel): string {
  return `Power ${power} of 3, ${POWER_LABELS[power]}`;
}

export type PunchTarget = "Head" | "Body";

export const PUNCH_TARGETS: readonly PunchTarget[] = ["Head", "Body"];

/**
 * Range is always derived from punch × target and shown read-only. Making it
 * user-settable would let a score describe a physically incoherent exchange.
 */
export type BoxingRange = "long" | "medium" | "close";

export const BOXING_RANGES: readonly BoxingRange[] = ["long", "medium", "close"];

export const BOXING_RANGE_LABELS: Readonly<Record<BoxingRange, string>> = {
  long: "Long",
  medium: "Medium",
  close: "Close",
};

export function defaultRange(punch: PunchNumber, target: PunchTarget): BoxingRange {
  if (punch === 1 || punch === 2) {
    return target === "Head" ? "long" : "medium";
  }
  if (punch === 3 || punch === 4) {
    return target === "Head" ? "medium" : "close";
  }
  return "close";
}

// ---------------------------------------------------------------------------
// Vocabulary — footwork and level
// ---------------------------------------------------------------------------

/**
 * Footwork is a property of a punch beat's context, never a beat of its own —
 * inserting a movement technique updates the selected beat instead of adding
 * one, so the numeric call stays readable.
 */
export type FootworkMove =
  | "Reset"
  | "Step in"
  | "Step out"
  | "Pivot lead"
  | "Pivot rear"
  | "Angle lead"
  | "Angle rear";

export const FOOTWORK_MOVES: readonly FootworkMove[] = [
  "Reset",
  "Step in",
  "Step out",
  "Pivot lead",
  "Pivot rear",
  "Angle lead",
  "Angle rear",
];

/**
 * Bag-friendly coaching cues. Each names one balanced movement rather than
 * implying a continual walk, which solo bag work cannot sustain.
 */
export const FOOTWORK_CUE_LABELS: Readonly<Record<FootworkMove, string>> = {
  Reset: "Reset the stance",
  "Step in": "Enter range, lead foot then rear",
  "Step out": "Exit range, rear foot then lead",
  "Pivot lead": "Pivot to the lead side",
  "Pivot rear": "Pivot to the rear side",
  "Angle lead": "Angle to the lead side",
  "Angle rear": "Angle to the rear side",
};

export const FOOTWORK_SCORE_LABELS: Readonly<Record<FootworkMove, string>> = {
  Reset: "RESET",
  "Step in": "ENTER",
  "Step out": "EXIT",
  "Pivot lead": "PIVOT",
  "Pivot rear": "PIVOT",
  "Angle lead": "ANGLE",
  "Angle rear": "ANGLE",
};

export type LevelTransition = "Change level down" | "Change level up";

export const LEVEL_TRANSITIONS: readonly LevelTransition[] = [
  "Change level down",
  "Change level up",
];

export const LEVEL_TRANSITION_SCORE_LABELS: Readonly<Record<LevelTransition, string>> = {
  "Change level down": "DOWN",
  "Change level up": "UP",
};

// ---------------------------------------------------------------------------
// Vocabulary — defense
// ---------------------------------------------------------------------------

export type DefenseResolution = "returnToStance" | "counter" | "exit";

export const DEFENSE_RESOLUTIONS: readonly DefenseResolution[] = [
  "returnToStance",
  "counter",
  "exit",
];

export const DEFENSE_RESOLUTION_LABELS: Readonly<Record<DefenseResolution, string>> = {
  returnToStance: "Return to stance",
  counter: "Counter",
  exit: "Exit safely",
};

/** Lower-case because these phrases are used verbatim inside sentences. */
export type DefenseThreat =
  | "lead straight to the head"
  | "rear straight to the head"
  | "straight punch to the head"
  | "lead hook to the head"
  | "rear hook to the head"
  | "hook to the head"
  | "punch to the body"
  | "uppercut to the chin"
  | "head combination";

export const DEFENSE_THREATS: readonly DefenseThreat[] = [
  "lead straight to the head",
  "rear straight to the head",
  "straight punch to the head",
  "lead hook to the head",
  "rear hook to the head",
  "hook to the head",
  "punch to the body",
  "uppercut to the chin",
  "head combination",
];

export const DEFENSE_THREAT_SPOKEN_LABELS: Readonly<Record<DefenseThreat, string>> = {
  "lead straight to the head": "the jab",
  "rear straight to the head": "the rear straight",
  "straight punch to the head": "a straight punch",
  "lead hook to the head": "the lead hook",
  "rear hook to the head": "the rear hook",
  "hook to the head": "a head hook",
  "punch to the body": "a body punch",
  "uppercut to the chin": "the uppercut",
  "head combination": "the head combination",
};

/**
 * The score's `vs …` chip has room for the noun only, so the article that makes
 * the spoken form read as a sentence is dropped for the visual label.
 */
export function threatChipLabel(threat: DefenseThreat): string {
  const spoken = DEFENSE_THREAT_SPOKEN_LABELS[threat];
  if (spoken.startsWith("the ")) return spoken.slice(4);
  if (spoken.startsWith("a ")) return spoken.slice(2);
  return spoken;
}

/** Ordered so `tier <= complexity` reads as a comparison, as it does in Swift. */
export type ComboSkillTier = 1 | 2 | 3;

export const SKILL_TIER_FUNDAMENTAL: ComboSkillTier = 1;
export const SKILL_TIER_INTERMEDIATE: ComboSkillTier = 2;
export const SKILL_TIER_ADVANCED: ComboSkillTier = 3;

export const SKILL_TIER_BADGES: Readonly<Record<ComboSkillTier, string>> = {
  1: "FUNDAMENTAL",
  2: "DEVELOPING",
  3: "TACTICAL ONLY",
};

/**
 * Stance-neutral defensive techniques. There is never a generic "Block" beat:
 * every move names the attack it answers, which is what keeps a generated cue
 * teachable when nobody is throwing at you.
 *
 * The four head-movement raw values are persisted in existing favorites and
 * must not be renamed.
 */
export type DefenseMove =
  | "Slip lead"
  | "Slip rear"
  | "Roll lead"
  | "Roll rear"
  | "Catch"
  | "Outside parry"
  | "Lead head block"
  | "Rear head block"
  | "Lead body block"
  | "Rear body block"
  | "Cover"
  | "Uppercut block"
  | "Shoulder block";

export const DEFENSE_HEAD_MOVEMENT_MOVES: readonly DefenseMove[] = [
  "Slip lead",
  "Slip rear",
  "Roll lead",
  "Roll rear",
];

export const DEFENSE_BLOCK_MOVES: readonly DefenseMove[] = [
  "Catch",
  "Outside parry",
  "Lead head block",
  "Rear head block",
  "Lead body block",
  "Rear body block",
  "Cover",
  "Uppercut block",
  "Shoulder block",
];

export const DEFENSE_MOVES: readonly DefenseMove[] = [
  ...DEFENSE_HEAD_MOVEMENT_MOVES,
  ...DEFENSE_BLOCK_MOVES,
];

export function isBlockMove(move: DefenseMove): boolean {
  return DEFENSE_BLOCK_MOVES.includes(move);
}

export function isHeadMovementMove(move: DefenseMove): boolean {
  return DEFENSE_HEAD_MOVEMENT_MOVES.includes(move);
}

export const DEFENSE_SHORT_LABELS: Readonly<Record<DefenseMove, string>> = {
  "Slip lead": "SL L",
  "Slip rear": "SL R",
  "Roll lead": "RL L",
  "Roll rear": "RL R",
  Catch: "CATCH",
  "Outside parry": "PARRY",
  "Lead head block": "LD HEAD",
  "Rear head block": "RR HEAD",
  "Lead body block": "LD BODY",
  "Rear body block": "RR BODY",
  Cover: "COVER",
  "Uppercut block": "UP BLOCK",
  "Shoulder block": "SHOULDER",
};

export const DEFENSE_SPOKEN_LABELS: Readonly<Record<DefenseMove, string>> = {
  // Slips and rolls speak their raw value; it is already an instruction.
  "Slip lead": "Slip lead",
  "Slip rear": "Slip rear",
  "Roll lead": "Roll lead",
  "Roll rear": "Roll rear",
  Catch: "Catch the jab",
  "Outside parry": "Outside parry the straight punch",
  "Lead head block": "Block the rear hook with the lead arm",
  "Rear head block": "Block the lead hook with the rear arm",
  "Lead body block": "Lead-side body block",
  "Rear body block": "Rear-side body block",
  Cover: "Cover the head combination",
  "Uppercut block": "Block the uppercut",
  "Shoulder block": "Shoulder block the rear straight",
};

export const DEFENSE_IMPLIED_THREATS: Readonly<Record<DefenseMove, DefenseThreat>> = {
  "Slip lead": "straight punch to the head",
  "Slip rear": "straight punch to the head",
  "Roll lead": "hook to the head",
  "Roll rear": "hook to the head",
  Catch: "lead straight to the head",
  "Outside parry": "straight punch to the head",
  "Lead head block": "rear hook to the head",
  "Rear head block": "lead hook to the head",
  "Lead body block": "punch to the body",
  "Rear body block": "punch to the body",
  Cover: "head combination",
  "Uppercut block": "uppercut to the chin",
  "Shoulder block": "rear straight to the head",
};

/**
 * A closed list per move. A threat outside it is not merely unusual — it
 * describes a defense that does not answer the attack, so normalization resets
 * it rather than storing an incoherent pairing.
 */
export const DEFENSE_COMPATIBLE_THREATS: Readonly<Record<DefenseMove, readonly DefenseThreat[]>> = {
  "Slip lead": [
    "lead straight to the head",
    "rear straight to the head",
    "straight punch to the head",
  ],
  "Slip rear": [
    "lead straight to the head",
    "rear straight to the head",
    "straight punch to the head",
  ],
  "Roll lead": ["lead hook to the head", "rear hook to the head", "hook to the head"],
  "Roll rear": ["lead hook to the head", "rear hook to the head", "hook to the head"],
  Catch: ["lead straight to the head"],
  "Outside parry": [
    "lead straight to the head",
    "rear straight to the head",
    "straight punch to the head",
  ],
  "Lead head block": ["rear hook to the head", "hook to the head"],
  "Rear head block": ["lead hook to the head", "hook to the head"],
  "Lead body block": ["punch to the body"],
  "Rear body block": ["punch to the body"],
  Cover: [
    "head combination",
    "lead hook to the head",
    "rear hook to the head",
    "hook to the head",
  ],
  "Uppercut block": ["uppercut to the chin"],
  "Shoulder block": ["rear straight to the head"],
};

/**
 * Required and resulting range are equal for every move: a defense holds
 * position rather than changing the distance, so the following punch inherits a
 * coherent range.
 */
export const DEFENSE_RANGES: Readonly<Record<DefenseMove, BoxingRange>> = {
  Catch: "long",
  "Outside parry": "long",
  "Slip lead": "long",
  "Slip rear": "long",
  "Shoulder block": "long",
  "Lead head block": "medium",
  "Rear head block": "medium",
  Cover: "medium",
  "Roll lead": "medium",
  "Roll rear": "medium",
  "Lead body block": "close",
  "Rear body block": "close",
  "Uppercut block": "close",
};

export const DEFENSE_SKILL_TIERS: Readonly<Record<DefenseMove, ComboSkillTier>> = {
  Catch: 1,
  "Outside parry": 1,
  Cover: 1,
  "Slip lead": 1,
  "Slip rear": 1,
  "Roll lead": 2,
  "Roll rear": 2,
  "Lead head block": 2,
  "Rear head block": 2,
  "Lead body block": 2,
  "Rear body block": 2,
  "Uppercut block": 3,
  "Shoulder block": 3,
};

/**
 * `Cover` is deliberately empty: it is a protected reset, so the generator must
 * not chain an automatic counter out of it.
 */
export const DEFENSE_PREFERRED_COUNTERS: Readonly<Record<DefenseMove, readonly PunchNumber[]>> = {
  Catch: [2, 1],
  "Outside parry": [2, 1],
  "Slip lead": [2, 1],
  "Slip rear": [1, 3],
  "Roll lead": [3, 2],
  "Roll rear": [4, 1],
  "Lead head block": [3, 2],
  "Rear head block": [2, 4],
  "Lead body block": [3, 2, 5],
  "Rear body block": [2, 4, 6],
  Cover: [],
  "Uppercut block": [2, 3],
  "Shoulder block": [2, 4],
};

// ---------------------------------------------------------------------------
// Beats
// ---------------------------------------------------------------------------

/**
 * A beat is a punch or a defense, never both and never neither. Splitting the
 * context per variant is what makes "a defense with a target" or "a punch with
 * a defensive resolution" unrepresentable instead of merely normalized away.
 *
 * `levelTransition` stays on both variants because it participates in
 * `sequenceKey`: a legacy favorite that carries one on a defensive beat must
 * keep the same identity after a decode round trip.
 */
export type ComboBeat = PunchBeat | DefenseBeat;

export interface PunchBeat {
  readonly kind: "punch";
  readonly id: ComboBeatId;
  readonly punch: PunchNumber;
  readonly power: PowerLevel;
  readonly target: PunchTarget;
  /** Always `defaultRange(punch, target)`; stored so identity survives a decode. */
  readonly range: BoxingRange;
  readonly movement: FootworkMove | null;
  readonly levelTransition: LevelTransition | null;
  readonly isLocked: boolean;
}

export interface DefenseBeat {
  readonly kind: "defense";
  readonly id: ComboBeatId;
  readonly move: DefenseMove;
  /** Always `DEFENSE_RANGES[move]`. */
  readonly range: BoxingRange;
  readonly movement: FootworkMove | null;
  readonly levelTransition: LevelTransition | null;
  readonly resolution: DefenseResolution;
  readonly isLocked: boolean;
}

/** A beat before it has been given identity — what an insert or a chunk carries. */
export type ComboBeatDraft =
  | Omit<PunchBeat, "id" | "isLocked">
  | Omit<DefenseBeat, "id" | "isLocked">;

/**
 * Contexts saved before targets existed were generated as head work, so a
 * missing target migrates to head rather than to "unknown".
 */
export function punchBeatDefaults(punch: PunchNumber): {
  readonly target: PunchTarget;
  readonly range: BoxingRange;
} {
  return { target: "Head", range: defaultRange(punch, "Head") };
}

export function defenseBeatDefaults(move: DefenseMove): {
  readonly range: BoxingRange;
  readonly resolution: DefenseResolution;
} {
  // Cover is a protected reset, so it returns to stance; every other defense is
  // authored expecting the counter that its preferred counters describe.
  return {
    range: DEFENSE_RANGES[move],
    resolution: move === "Cover" ? "returnToStance" : "counter",
  };
}

export function beatSpokenLabel(beat: ComboBeat): string {
  if (beat.kind === "punch") {
    return beat.power === POWER_SHARP
      ? `${beat.punch}`
      : `${POWER_LABELS[beat.power]} ${beat.punch}`;
  }
  return DEFENSE_SPOKEN_LABELS[beat.move];
}

/** The one-line identity used by inspectors, transports and rehearsal cards. */
export function shortBeatLabel(beat: ComboBeat, threat: DefenseThreat): string;
export function shortBeatLabel(beat: PunchBeat): string;
export function shortBeatLabel(beat: ComboBeat, threat?: DefenseThreat): string {
  if (beat.kind === "punch") {
    return `${punchName(beat.punch)}, ${beat.target.toLowerCase()}, P${beat.power}`;
  }
  return `${beat.move} against ${threat ?? DEFENSE_IMPLIED_THREATS[beat.move]}`;
}

export function beatScoreLabel(beat: ComboBeat): string {
  return beat.kind === "punch" ? `${beat.punch}` : DEFENSE_SHORT_LABELS[beat.move];
}

// ---------------------------------------------------------------------------
// Shape
// ---------------------------------------------------------------------------

export type ComboFlow = "Familiar" | "Varied" | "Exploratory";
export type ComboTargetIntent = "Head" | "Change levels" | "Body focus";
export type ComboMovementIntent = "Set" | "Angle" | "Mobile";
export type ComboDefenseIntent = "None" | "Defend" | "Counter";
export type ComboDefenseFamily = "Blocks" | "Head movement" | "Mixed";
export type ComboPowerFinish = "Measured" | "One accent" | "Power finish";

export const COMBO_FLOWS: readonly ComboFlow[] = ["Familiar", "Varied", "Exploratory"];
export const COMBO_TARGET_INTENTS: readonly ComboTargetIntent[] = [
  "Head",
  "Change levels",
  "Body focus",
];
export const COMBO_MOVEMENT_INTENTS: readonly ComboMovementIntent[] = ["Set", "Angle", "Mobile"];
export const COMBO_DEFENSE_INTENTS: readonly ComboDefenseIntent[] = ["None", "Defend", "Counter"];
export const COMBO_DEFENSE_FAMILIES: readonly ComboDefenseFamily[] = [
  "Blocks",
  "Head movement",
  "Mixed",
];
export const COMBO_POWER_FINISHES: readonly ComboPowerFinish[] = [
  "Measured",
  "One accent",
  "Power finish",
];

export interface ComboShape {
  readonly flow: ComboFlow;
  readonly target: ComboTargetIntent;
  readonly movement: ComboMovementIntent;
  readonly defense: ComboDefenseIntent;
  readonly defenseFamily: ComboDefenseFamily;
  readonly powerFinish: ComboPowerFinish;
}

/** The shape assumed for a favorite saved before shapes existed. */
export const LEGACY_COMBO_SHAPE: ComboShape = {
  flow: "Familiar",
  target: "Head",
  movement: "Set",
  defense: "None",
  defenseFamily: "Head movement",
  powerFinish: "Measured",
};

/**
 * Reconstructs a shape by reading what the stored beats already do, so an old
 * favorite reopened in the Studio shows constraints that match it instead of
 * defaults that would remix it into something else.
 */
export function inferredLegacyShape(beats: readonly ComboBeat[]): ComboShape {
  const defenses = beats.filter((beat): beat is DefenseBeat => beat.kind === "defense");
  let defenseFamily: ComboDefenseFamily;
  if (defenses.length === 0) {
    defenseFamily = "Head movement";
  } else if (defenses.every((beat) => isBlockMove(beat.move))) {
    defenseFamily = "Blocks";
  } else if (defenses.every((beat) => isHeadMovementMove(beat.move))) {
    defenseFamily = "Head movement";
  } else {
    defenseFamily = "Mixed";
  }

  const powers = beats
    .filter((beat): beat is PunchBeat => beat.kind === "punch")
    .map((beat) => beat.power);
  const lastPower = powers.length > 0 ? powers[powers.length - 1] : undefined;
  let powerFinish: ComboPowerFinish;
  if (lastPower === POWER_FULL) {
    powerFinish = "Power finish";
  } else if (powers.includes(POWER_FULL)) {
    powerFinish = "One accent";
  } else {
    powerFinish = "Measured";
  }

  return {
    flow: "Familiar",
    target: "Head",
    movement: "Set",
    defense: defenses.length === 0 ? "None" : "Counter",
    defenseFamily,
    powerFinish,
  };
}

// ---------------------------------------------------------------------------
// Phrase brackets
// ---------------------------------------------------------------------------

export const PHRASE_BRACKET_MIN_BEATS = 2;
export const PHRASE_REPEAT_MIN = 1;
export const PHRASE_REPEAT_MAX = 12;
export const PHRASE_DEFAULT_NAME = "Phrase";

/**
 * A named, repeatable chunk. Beat identity rather than offsets keeps the
 * bracket attached while neighbouring beats move.
 */
export interface ComboPhraseBracket {
  readonly id: PhraseBracketId;
  readonly name: string;
  /** Always ≥ 2 beats, contiguous, and stored in score order. */
  readonly beatIds: readonly ComboBeatId[];
  readonly repeatCount: number;
}

export function clampPhraseRepeatCount(value: number): number {
  return Math.min(PHRASE_REPEAT_MAX, Math.max(PHRASE_REPEAT_MIN, Math.trunc(value)));
}

export function phraseBracketName(raw: string): string {
  const trimmed = raw.trim();
  return trimmed.length === 0 ? PHRASE_DEFAULT_NAME : trimmed;
}

export function phraseBracketLabel(bracket: ComboPhraseBracket): string {
  const name = bracket.name.toUpperCase();
  return bracket.repeatCount > 1 ? `${name} ×${bracket.repeatCount}` : name;
}

/**
 * A phrase describes a chunk you can actually repeat, so a selection that skips
 * a beat in the middle is not a smaller phrase — it is not a phrase at all.
 */
export function isContiguousSelection(
  beatIds: readonly ComboBeatId[],
  order: readonly ComboBeatId[],
): boolean {
  const indices = resolveBeatIndices(beatIds, order);
  if (indices.length < PHRASE_BRACKET_MIN_BEATS) return false;
  const first = indices[0];
  const last = indices[indices.length - 1];
  if (first === undefined || last === undefined) return false;
  return last - first + 1 === indices.length;
}

/**
 * Deduplicates preserving first occurrence, then reorders to score order —
 * the order the user tapped capsules in must not change what the phrase means.
 */
export function resolveBeatIndices(
  beatIds: readonly ComboBeatId[],
  order: readonly ComboBeatId[],
): readonly number[] {
  const positions = new Map<ComboBeatId, number>();
  order.forEach((id, index) => positions.set(id, index));
  const seen = new Set<ComboBeatId>();
  const indices: number[] = [];
  for (const id of beatIds) {
    if (seen.has(id)) continue;
    seen.add(id);
    const index = positions.get(id);
    if (index !== undefined) indices.push(index);
  }
  return indices.sort((a, b) => a - b);
}

// ---------------------------------------------------------------------------
// Response branches
// ---------------------------------------------------------------------------

export const RESPONSE_OPTION_LIMIT = 2;
export const RESPONSE_DEFAULT_NAME = "Response";

/**
 * One explicitly planned solo response. It is not an observed or inferred
 * reaction; the athlete chooses it before rehearsal, which is the only claim
 * this app is entitled to make.
 */
export interface ComboResponseOption {
  readonly id: ResponseOptionId;
  readonly name: string;
  /** Never locked: a branch is a rehearsal aid, not part of the locked score. */
  readonly beats: readonly ComboBeat[];
}

/** At most one branch per defensive beat, at most two options per branch. */
export interface ComboResponseBranch {
  readonly id: ResponseBranchId;
  readonly defenseBeatId: ComboBeatId;
  readonly threat: DefenseThreat;
  readonly options: readonly ComboResponseOption[];
  readonly selectedOptionId: ResponseOptionId | null;
}

export function responseOptionName(raw: string): string {
  const trimmed = raw.trim();
  return trimmed.length === 0 ? RESPONSE_DEFAULT_NAME : trimmed;
}

export function responseOptionSpokenLabel(option: ComboResponseOption): string {
  return option.beats.map(beatSpokenLabel).join(", ");
}

export function selectedResponseOption(
  branch: ComboResponseBranch,
): ComboResponseOption | null {
  return branch.options.find((option) => option.id === branch.selectedOptionId) ?? null;
}

export function responseBranchFor(
  score: ComboScore,
  beatId: ComboBeatId,
): ComboResponseBranch | null {
  return score.responseBranches.find((branch) => branch.defenseBeatId === beatId) ?? null;
}

/** Cover has no preferred counter, so its default branch returns the jab. */
export function defaultResponseCounter(move: DefenseMove): PunchNumber {
  return DEFENSE_PREFERRED_COUNTERS[move][0] ?? 1;
}

export function defaultResponseOptionName(counter: PunchNumber): string {
  return `Return ${counter}`;
}

/** The default branch is one sharp counter — an accent is a deliberate choice. */
export function defaultResponseBeatDraft(move: DefenseMove): ComboBeatDraft {
  const counter = defaultResponseCounter(move);
  return {
    kind: "punch",
    punch: counter,
    power: POWER_SHARP,
    target: "Head",
    range: defaultRange(counter, "Head"),
    movement: null,
    levelTransition: null,
  };
}

// ---------------------------------------------------------------------------
// The score
// ---------------------------------------------------------------------------

export const COMPLEXITY_MIN = 1;
export const COMPLEXITY_MAX = 3;

export type ComboComplexity = 1 | 2 | 3;

export const COMPLEXITY_LABELS: Readonly<Record<ComboComplexity, string>> = {
  1: "Focused",
  2: "Layered",
  3: "Tactical",
};

/** Punch-count budgets per complexity. Dosage in time or reps lives elsewhere. */
export const COMPLEXITY_PUNCH_COUNTS: Readonly<
  Record<ComboComplexity, { readonly min: number; readonly max: number }>
> = {
  1: { min: 2, max: 3 },
  2: { min: 3, max: 4 },
  3: { min: 4, max: 6 },
};

export function clampComplexity(value: number): ComboComplexity {
  const clamped = Math.min(COMPLEXITY_MAX, Math.max(COMPLEXITY_MIN, Math.trunc(value)));
  return clamped as ComboComplexity;
}

/**
 * The single value Compose, Rehearse and Ringside all read. It is normalized by
 * construction: contexts are derived, beat ids are unique, brackets are
 * contiguous, and every branch points at a real defensive beat.
 */
export interface ComboScore {
  readonly id: ComboId;
  readonly revision: ComboScoreRevision;
  readonly beats: readonly ComboBeat[];
  readonly complexity: ComboComplexity;
  readonly stance: BoxingStance;
  /** Epoch milliseconds. */
  readonly createdAt: Milliseconds;
  readonly shape: ComboShape;
  readonly phraseBrackets: readonly ComboPhraseBracket[];
  readonly responseBranches: readonly ComboResponseBranch[];
}

export function comboPunchBeats(score: ComboScore): readonly PunchBeat[] {
  return score.beats.filter((beat): beat is PunchBeat => beat.kind === "punch");
}

/** Combo Lab's "{n} punches" excludes defenses, which are not punches thrown. */
export function comboPunchCount(score: ComboScore): number {
  return comboPunchBeats(score).length;
}

export function comboPowerCount(score: ComboScore): number {
  return comboPunchBeats(score).filter((beat) => beat.power === POWER_FULL).length;
}

export function comboLockedCount(score: ComboScore): number {
  return score.beats.filter((beat) => beat.isLocked).length;
}

export function comboNumericLabel(score: ComboScore): string {
  return score.beats.map(beatScoreLabel).join("  ");
}

export function comboBeatById(score: ComboScore, id: ComboBeatId): ComboBeat | null {
  return score.beats.find((beat) => beat.id === id) ?? null;
}

export function comboBeatIndex(score: ComboScore, id: ComboBeatId): number | null {
  const index = score.beats.findIndex((beat) => beat.id === id);
  return index < 0 ? null : index;
}

/**
 * The spoken call.
 *
 * Entry and level changes are spoken before the punch and exits, pivots, angles
 * and resets after it, because that is the order the body performs them. The
 * target is only named when it is new information — body work, a change from
 * the previous punch, or the first punch after a defense — so a head-only
 * phrase does not become "1 to the head, 2 to the head".
 */
export function comboVoicePrompt(score: ComboScore): string {
  let priorTarget: PunchTarget | null = null;
  let previousWasDefense = false;
  const calls: string[] = [];

  for (const beat of score.beats) {
    if (beat.kind === "punch") {
      const base =
        beat.power === POWER_SHARP
          ? `${beat.punch}`
          : `${POWER_LABELS[beat.power]} ${beat.punch}`;
      const statesTarget =
        beat.target === "Body" ||
        (priorTarget !== null && beat.target !== priorTarget) ||
        previousWasDefense;
      const targetCall = statesTarget ? ` to the ${beat.target.toLowerCase()}` : "";

      const setup: string[] = [];
      if (beat.movement === "Step in") setup.push(FOOTWORK_CUE_LABELS["Step in"]);
      if (beat.levelTransition !== null) setup.push(beat.levelTransition);
      setup.push(`${base}${targetCall}`);

      const followThrough =
        beat.movement !== null && beat.movement !== "Step in"
          ? FOOTWORK_CUE_LABELS[beat.movement].toLowerCase()
          : null;

      calls.push(setup.join(", ") + (followThrough === null ? "" : `, ${followThrough}`));
      priorTarget = beat.target;
      previousWasDefense = false;
      continue;
    }

    previousWasDefense = true;
    const planned = responseBranchFor(score, beat.id)?.threat;
    if (planned !== undefined && planned !== DEFENSE_IMPLIED_THREATS[beat.move]) {
      // A planned threat that differs from the implied one is the whole point of
      // the branch, so the call has to name it or the rehearsal is ambiguous.
      calls.push(`${beat.move}, against ${DEFENSE_THREAT_SPOKEN_LABELS[planned]}`);
    } else {
      calls.push(DEFENSE_SPOKEN_LABELS[beat.move]);
    }
  }

  return calls.join(", ");
}

/**
 * Combo Lab announces the stance because the generator's stance picker sits in
 * a menu the user may not be looking at; the Studio shows stance persistently
 * and does not repeat it.
 */
export function comboLabVoicePrompt(score: ComboScore): string {
  const prompt = comboVoicePrompt(score);
  return score.stance === "Southpaw" ? `Southpaw. ${prompt}` : prompt;
}

/**
 * Stable identity for a movement sequence. Timestamps and generated ids are
 * deliberately excluded so the same favorite cannot be saved twice; brackets,
 * branches and locks are excluded too, so re-saving a structurally identical
 * score updates the stored copy in place instead of duplicating it.
 */
export function comboSequenceKey(score: ComboScore): string {
  const keys = score.beats.map((beat) => {
    const contextKey = [
      beat.kind === "punch" ? beat.target : "-",
      beat.range,
      beat.movement ?? "-",
      beat.levelTransition ?? "-",
      beat.kind === "defense" ? beat.resolution : "-",
    ].join(":");
    return beat.kind === "punch"
      ? `p${beat.punch}-${beat.power}-${contextKey}`
      : `d-${beat.move}-${DEFENSE_IMPLIED_THREATS[beat.move]}-${contextKey}`;
  });
  return `${score.stance}|${keys.join("|")}`;
}

// ---------------------------------------------------------------------------
// Deterministic beat identity
// ---------------------------------------------------------------------------

const GOLDEN_RATIO_64 = 0x9e3779b97f4a7c15n;
const MASK_64 = (1n << 64n) - 1n;
const MASK_48 = 0x0000ffffffffffffn;

/**
 * Derives a beat id for a favorite saved before beats had identity.
 *
 * The derivation is a pure function of the score id and the beat's position, so
 * a re-decode reproduces exactly the same ids — which is what stops a legacy
 * favorite from losing its locks, brackets and branches on every launch. The
 * hex is upper case because the Swift app formats UUIDs that way and the two
 * platforms must agree on the string.
 */
export function deriveBeatId(namespace: ComboId | ResponseOptionId, index: number): ComboBeatId {
  const components = namespace.split("-");
  const tailPart = components[4];
  if (components.length !== 5 || tailPart === undefined || !/^[0-9a-fA-F]{12}$/.test(tailPart)) {
    // A namespace that is not a UUID cannot be mixed, but the result still has
    // to be unique per index: collision resolution retries with a higher index
    // and would otherwise be handed the same string forever.
    return comboBeatId(`${namespace}-${index + 1}`);
  }
  const tail = BigInt(`0x${tailPart}`);
  const mixed = ((tail * GOLDEN_RATIO_64) & MASK_64) ^ BigInt(index + 1);
  const replacementTail = (mixed & MASK_48).toString(16).toUpperCase().padStart(12, "0");
  return comboBeatId(
    `${components[0]}-${components[1]}-${components[2]}-${components[3]}-${replacementTail}`,
  );
}

/**
 * Resolves the id for every position, deriving what is missing and retrying
 * collisions from `index + count + 1` so two beats can never share identity.
 */
export function normalizeBeatIds(
  proposed: readonly (ComboBeatId | undefined)[],
  namespace: ComboId,
  count: number,
): readonly ComboBeatId[] {
  const used = new Set<ComboBeatId>();
  const resolved: ComboBeatId[] = [];
  for (let index = 0; index < count; index += 1) {
    const candidate = proposed[index] ?? deriveBeatId(namespace, index);
    if (!used.has(candidate)) {
      used.add(candidate);
      resolved.push(candidate);
      continue;
    }
    let collisionIndex = index + count + 1;
    let replacement = deriveBeatId(namespace, collisionIndex);
    while (used.has(replacement)) {
      collisionIndex += 1;
      replacement = deriveBeatId(namespace, collisionIndex);
    }
    used.add(replacement);
    resolved.push(replacement);
  }
  return resolved;
}

// ---------------------------------------------------------------------------
// Normalization
// ---------------------------------------------------------------------------

/** What a decoder or a generator hands over before invariants are enforced. */
export interface ComboScoreDraft {
  readonly id: ComboId;
  readonly beats: readonly ComboBeat[];
  readonly complexity: number;
  readonly stance: BoxingStance;
  readonly createdAt: Milliseconds;
  readonly shape: ComboShape | null;
  readonly phraseBrackets: readonly ComboPhraseBracket[];
  readonly responseBranches: readonly ComboResponseBranch[];
}

/** Re-derives every value the user is not allowed to author directly. */
export function normalizeBeat(beat: ComboBeat): ComboBeat {
  if (beat.kind === "punch") {
    return { ...beat, range: defaultRange(beat.punch, beat.target) };
  }
  return { ...beat, range: DEFENSE_RANGES[beat.move] };
}

export function normalizePhraseBrackets(
  brackets: readonly ComboPhraseBracket[],
  order: readonly ComboBeatId[],
): readonly ComboPhraseBracket[] {
  const usedIds = new Set<PhraseBracketId>();
  const normalized: ComboPhraseBracket[] = [];
  for (const bracket of brackets) {
    if (usedIds.has(bracket.id)) continue;
    const indices = resolveBeatIndices(bracket.beatIds, order);
    const first = indices[0];
    const last = indices[indices.length - 1];
    if (
      indices.length < PHRASE_BRACKET_MIN_BEATS ||
      first === undefined ||
      last === undefined ||
      last - first + 1 !== indices.length
    ) {
      // A bracket whose beats no longer form a chunk is dropped rather than
      // redrawn across a gap it never described.
      continue;
    }
    usedIds.add(bracket.id);
    const beatIds: ComboBeatId[] = [];
    for (const index of indices) {
      const id = order[index];
      if (id !== undefined) beatIds.push(id);
    }
    normalized.push({
      id: bracket.id,
      name: phraseBracketName(bracket.name),
      beatIds,
      repeatCount: clampPhraseRepeatCount(bracket.repeatCount),
    });
  }
  return normalized;
}

export function normalizeResponseBranches(
  branches: readonly ComboResponseBranch[],
  beats: readonly ComboBeat[],
): readonly ComboResponseBranch[] {
  const defenseByBeatId = new Map<ComboBeatId, DefenseBeat>();
  for (const beat of beats) {
    if (beat.kind === "defense") defenseByBeatId.set(beat.id, beat);
  }

  const usedBranchIds = new Set<ResponseBranchId>();
  const claimedBeats = new Set<ComboBeatId>();
  const normalized: ComboResponseBranch[] = [];

  for (const branch of branches) {
    if (usedBranchIds.has(branch.id) || claimedBeats.has(branch.defenseBeatId)) continue;
    const defense = defenseByBeatId.get(branch.defenseBeatId);
    if (defense === undefined) continue;

    usedBranchIds.add(branch.id);
    claimedBeats.add(branch.defenseBeatId);

    const compatible = DEFENSE_COMPATIBLE_THREATS[defense.move];
    const threat = compatible.includes(branch.threat)
      ? branch.threat
      : DEFENSE_IMPLIED_THREATS[defense.move];

    const usedOptionIds = new Set<ResponseOptionId>();
    const options: ComboResponseOption[] = [];
    for (const option of branch.options.slice(0, RESPONSE_OPTION_LIMIT)) {
      if (usedOptionIds.has(option.id) || option.beats.length === 0) continue;
      usedOptionIds.add(option.id);
      const usedBeatIds = new Set<ComboBeatId>();
      const optionBeats: ComboBeat[] = [];
      option.beats.forEach((beat, index) => {
        let id = beat.id;
        if (usedBeatIds.has(id)) {
          let collisionIndex = index + option.beats.length + 1;
          id = deriveBeatId(option.id, collisionIndex);
          while (usedBeatIds.has(id)) {
            collisionIndex += 1;
            id = deriveBeatId(option.id, collisionIndex);
          }
        }
        usedBeatIds.add(id);
        // Response beats are never locked: locking is a Compose affordance for
        // the score itself, and a locked branch beat could not be remixed away.
        optionBeats.push(normalizeBeat({ ...beat, id, isLocked: false }));
      });
      options.push({
        id: option.id,
        name: responseOptionName(option.name),
        beats: optionBeats,
      });
    }

    const selected = options.find((option) => option.id === branch.selectedOptionId);
    normalized.push({
      id: branch.id,
      defenseBeatId: branch.defenseBeatId,
      threat,
      options,
      selectedOptionId: selected?.id ?? options[0]?.id ?? null,
    });
  }

  return normalized;
}

/**
 * The single constructor for a score. Every editing transaction rebuilds
 * through it, so an invariant fixed here cannot be bypassed by an edit path.
 */
export function normalizeComboScore(
  draft: ComboScoreDraft,
  revision: ComboScoreRevision,
): ComboScore {
  const ids = normalizeBeatIds(
    draft.beats.map((beat) => beat.id),
    draft.id,
    draft.beats.length,
  );
  const beats = draft.beats.map((beat, index) =>
    normalizeBeat({ ...beat, id: ids[index] ?? beat.id }),
  );
  const order = beats.map((beat) => beat.id);
  return {
    id: draft.id,
    revision,
    beats,
    complexity: clampComplexity(draft.complexity),
    stance: draft.stance,
    createdAt: draft.createdAt,
    shape: draft.shape ?? inferredLegacyShape(beats),
    phraseBrackets: normalizePhraseBrackets(draft.phraseBrackets, order),
    responseBranches: normalizeResponseBranches(draft.responseBranches, beats),
  };
}

// ---------------------------------------------------------------------------
// Persisted shape
// ---------------------------------------------------------------------------

export type PersistedComboAction =
  | { readonly kind: "punch"; readonly punch: PunchNumber; readonly power: PowerLevel }
  | { readonly kind: "defense"; readonly move: DefenseMove };

export interface PersistedComboBeatContext {
  readonly target?: PunchTarget | null;
  readonly range?: BoxingRange;
  readonly movement?: FootworkMove | null;
  readonly levelTransition?: LevelTransition | null;
  readonly defenseResolution?: DefenseResolution | null;
}

/**
 * The on-disk record. Actions, contexts and ids stay as parallel arrays because
 * that is the shape existing favorites were written in; every field is optional
 * because the decoder must never reject a stored score — a missing key migrates,
 * it does not discard the user's work.
 */
export interface PersistedComboScore {
  readonly id?: string;
  readonly actions?: readonly PersistedComboAction[];
  readonly complexity?: number;
  readonly stance?: BoxingStance;
  readonly createdAt?: Milliseconds;
  readonly beatContexts?: readonly PersistedComboBeatContext[];
  readonly shape?: ComboShape;
  readonly beatIDs?: readonly string[];
  readonly lockedBeatIDs?: readonly string[];
  readonly phraseBrackets?: readonly {
    readonly id: string;
    readonly name?: string;
    readonly beatIDs?: readonly string[];
    readonly repeatCount?: number;
  }[];
  readonly responseBranches?: readonly {
    readonly id: string;
    readonly defenseBeatID: string;
    readonly threat: DefenseThreat;
    readonly options?: readonly {
      readonly id: string;
      readonly name?: string;
      readonly beats?: readonly {
        readonly id: string;
        readonly action: PersistedComboAction;
        readonly context?: PersistedComboBeatContext;
      }[];
    }[];
    readonly selectedOptionID?: string | null;
  }[];
}

export function persistComboScore(score: ComboScore): PersistedComboScore {
  return {
    id: score.id,
    actions: score.beats.map((beat) =>
      beat.kind === "punch"
        ? { kind: "punch", punch: beat.punch, power: beat.power }
        : { kind: "defense", move: beat.move },
    ),
    complexity: score.complexity,
    stance: score.stance,
    createdAt: score.createdAt,
    beatContexts: score.beats.map((beat) => ({
      target: beat.kind === "punch" ? beat.target : null,
      range: beat.range,
      movement: beat.movement,
      levelTransition: beat.levelTransition,
      defenseResolution: beat.kind === "defense" ? beat.resolution : null,
    })),
    shape: score.shape,
    beatIDs: score.beats.map((beat) => beat.id),
    lockedBeatIDs: score.beats.filter((beat) => beat.isLocked).map((beat) => beat.id),
    phraseBrackets: score.phraseBrackets.map((bracket) => ({
      id: bracket.id,
      name: bracket.name,
      beatIDs: bracket.beatIds,
      repeatCount: bracket.repeatCount,
    })),
    responseBranches: score.responseBranches.map((branch) => ({
      id: branch.id,
      defenseBeatID: branch.defenseBeatId,
      threat: branch.threat,
      options: branch.options.map((option) => ({
        id: option.id,
        name: option.name,
        beats: option.beats.map((beat) => ({
          id: beat.id,
          action:
            beat.kind === "punch"
              ? ({ kind: "punch", punch: beat.punch, power: beat.power } as const)
              : ({ kind: "defense", move: beat.move } as const),
          context: {
            target: beat.kind === "punch" ? beat.target : null,
            range: beat.range,
            movement: beat.movement,
            levelTransition: beat.levelTransition,
            defenseResolution: beat.kind === "defense" ? beat.resolution : null,
          },
        })),
      })),
      selectedOptionID: branch.selectedOptionId,
    })),
  };
}

/**
 * Rebuilds a beat from a stored action plus whatever context survived. A
 * context shorter than the action array back-fills with the legacy default,
 * which is how pre-target favorites migrate to head work.
 */
export function hydrateComboBeat(
  id: ComboBeatId,
  action: PersistedComboAction,
  context: PersistedComboBeatContext | undefined,
  isLocked: boolean,
): ComboBeat {
  if (action.kind === "punch") {
    const target = context?.target ?? "Head";
    return {
      kind: "punch",
      id,
      punch: action.punch,
      power: action.power,
      target,
      range: defaultRange(action.punch, target),
      movement: context?.movement ?? null,
      levelTransition: context?.levelTransition ?? null,
      isLocked,
    };
  }
  const defaults = defenseBeatDefaults(action.move);
  return {
    kind: "defense",
    id,
    move: action.move,
    range: defaults.range,
    movement: context?.movement ?? null,
    levelTransition: context?.levelTransition ?? null,
    resolution: context?.defenseResolution ?? defaults.resolution,
    isLocked,
  };
}

// ---------------------------------------------------------------------------
// Generator parameters
// ---------------------------------------------------------------------------

/**
 * The generator's constraints. There is deliberately no time or repetition
 * field here: combo surfaces never prescribe dosage — that belongs to the
 * interval editor alone.
 */
export interface ComboParameters {
  readonly complexity: ComboComplexity;
  readonly stance: BoxingStance;
  readonly enabledPunches: readonly PunchNumber[];
  readonly featuredPunches: readonly PunchNumber[];
  readonly includesSlips: boolean;
  readonly includesRolls: boolean;
  readonly requiresDefense: boolean;
  readonly variesPower: boolean;
  readonly finalPowerAccent: boolean;
  readonly flow: ComboFlow;
  readonly targetIntent: ComboTargetIntent;
  readonly movementIntent: ComboMovementIntent;
  readonly defenseIntent: ComboDefenseIntent;
  readonly defenseFamily: ComboDefenseFamily;
  readonly powerFinish: ComboPowerFinish;
  readonly enabledBlocks: readonly DefenseMove[];
}

export const DEFAULT_COMBO_PARAMETERS: ComboParameters = {
  complexity: 2,
  stance: "Orthodox",
  enabledPunches: [1, 2, 3, 4, 5, 6],
  featuredPunches: [],
  includesSlips: true,
  includesRolls: true,
  requiresDefense: false,
  variesPower: true,
  finalPowerAccent: false,
  flow: "Familiar",
  targetIntent: "Head",
  movementIntent: "Set",
  defenseIntent: "None",
  defenseFamily: "Mixed",
  powerFinish: "One accent",
  enabledBlocks: DEFENSE_BLOCK_MOVES,
};

/** An empty punch vocabulary still has to generate something throwable. */
export const FALLBACK_ENABLED_PUNCHES: readonly PunchNumber[] = [1, 2];

/**
 * `requiresDefense` predates the intent enum. An old preference record that
 * only says "defense required" means counter work with head movement, so the
 * resolved values differ from the stored ones rather than rewriting them.
 */
export function resolvedDefenseIntent(parameters: ComboParameters): ComboDefenseIntent {
  return parameters.requiresDefense && parameters.defenseIntent === "None"
    ? "Counter"
    : parameters.defenseIntent;
}

export function resolvedDefenseFamily(parameters: ComboParameters): ComboDefenseFamily {
  return parameters.requiresDefense && parameters.defenseIntent === "None"
    ? "Head movement"
    : parameters.defenseFamily;
}

/**
 * The power counterpart of the two defense resolvers, and the single answer to
 * "how does this recipe finish?".
 *
 * `variesPower` and `finalPowerAccent` predate the enum and remain independently
 * settable, so a parameter set can carry two contradictory power decisions at
 * once. F-CB-20 fixes the precedence and this is where it lives: an explicit
 * final accent wins outright, then a recipe that switched power variation off is
 * measured, and only then does the enum speak. Consumers — the generator, the
 * Combo Lab pattern summary, the Shape sheet — must all read this rather than
 * `parameters.powerFinish`, or a guided round stops being reproducible from its
 * plan alone.
 *
 * This is the runtime rule. {@link legacyPowerFinishDefault} is the *decode*
 * default for a key an older install never wrote; the two agree by construction
 * but they answer different questions.
 */
export function resolvedPowerFinish(parameters: ComboParameters): ComboPowerFinish {
  if (parameters.finalPowerAccent) return "Power finish";
  if (!parameters.variesPower) return "Measured";
  return parameters.powerFinish;
}

/**
 * Decode defaults for keys an older install never wrote. They intentionally
 * differ from `DEFAULT_COMBO_PARAMETERS`: a fresh install starts Mixed, while an
 * upgrade infers the family its legacy toggles actually described.
 */
export function legacyDefenseFamilyDefault(parameters: {
  readonly includesSlips: boolean;
  readonly includesRolls: boolean;
}): ComboDefenseFamily {
  return parameters.includesSlips || parameters.includesRolls ? "Head movement" : "Blocks";
}

export function legacyDefenseIntentDefault(requiresDefense: boolean): ComboDefenseIntent {
  return requiresDefense ? "Counter" : "None";
}

export function legacyPowerFinishDefault(parameters: {
  readonly finalPowerAccent: boolean;
  readonly variesPower: boolean;
}): ComboPowerFinish {
  if (parameters.finalPowerAccent) return "Power finish";
  return parameters.variesPower ? "One accent" : "Measured";
}

// ---------------------------------------------------------------------------
// Guided themes
// ---------------------------------------------------------------------------

/**
 * Each theme is a complete parameter recipe rather than a hint, so a guided
 * round's combos are reproducible from the plan alone.
 *
 * Every recipe states its power decision *consistently* across the legacy
 * booleans and the enum, so `resolvedPowerFinish` and `powerFinish` agree for
 * every theme and no consumer can read two different answers off one recipe.
 */
export const COMBO_THEME_PARAMETERS: Readonly<Record<GuidedComboTheme, ComboParameters>> = {
  straightShots: {
    ...DEFAULT_COMBO_PARAMETERS,
    complexity: 1,
    enabledPunches: [1, 2],
    includesSlips: false,
    includesRolls: false,
    variesPower: false,
    flow: "Familiar",
    targetIntent: "Head",
    defenseIntent: "None",
    powerFinish: "Measured",
  },
  leadHook: {
    ...DEFAULT_COMBO_PARAMETERS,
    complexity: 2,
    enabledPunches: [1, 2, 3],
    featuredPunches: [3],
    includesSlips: false,
    includesRolls: false,
    flow: "Familiar",
    targetIntent: "Head",
    defenseIntent: "None",
  },
  bodyWork: {
    ...DEFAULT_COMBO_PARAMETERS,
    complexity: 2,
    enabledPunches: [1, 2, 3, 4, 5, 6],
    flow: "Familiar",
    targetIntent: "Body focus",
    movementIntent: "Set",
    defenseIntent: "None",
    includesSlips: false,
    includesRolls: false,
  },
  blocks: {
    ...DEFAULT_COMBO_PARAMETERS,
    complexity: 2,
    enabledPunches: [1, 2, 3, 4],
    flow: "Familiar",
    targetIntent: "Change levels",
    movementIntent: "Set",
    defenseIntent: "Counter",
    defenseFamily: "Blocks",
    enabledBlocks: [
      "Catch",
      "Outside parry",
      "Lead head block",
      "Rear head block",
      "Lead body block",
      "Rear body block",
    ],
    includesSlips: false,
    includesRolls: false,
    requiresDefense: true,
  },
  slips: {
    ...DEFAULT_COMBO_PARAMETERS,
    complexity: 2,
    enabledPunches: [1, 2, 3],
    includesSlips: true,
    includesRolls: false,
    requiresDefense: true,
    defenseIntent: "Counter",
    defenseFamily: "Head movement",
  },
  uppercuts: {
    ...DEFAULT_COMBO_PARAMETERS,
    complexity: 2,
    enabledPunches: [1, 2, 3, 5, 6],
    featuredPunches: [5, 6],
    includesSlips: false,
    includesRolls: false,
  },
  fastHands: {
    ...DEFAULT_COMBO_PARAMETERS,
    complexity: 2,
    enabledPunches: [1, 2, 3],
    includesSlips: false,
    includesRolls: false,
    variesPower: false,
    powerFinish: "Measured",
  },
  rolls: {
    ...DEFAULT_COMBO_PARAMETERS,
    complexity: 3,
    enabledPunches: [1, 2, 3, 4],
    featuredPunches: [3, 4],
    includesSlips: false,
    includesRolls: true,
    requiresDefense: true,
    defenseIntent: "Counter",
    defenseFamily: "Head movement",
    movementIntent: "Angle",
  },
  powerAccents: {
    ...DEFAULT_COMBO_PARAMETERS,
    complexity: 3,
    includesSlips: false,
    includesRolls: false,
    variesPower: false,
    finalPowerAccent: true,
    powerFinish: "Power finish",
  },
  freestyle: {
    ...DEFAULT_COMBO_PARAMETERS,
    complexity: 3,
    flow: "Varied",
    targetIntent: "Change levels",
    movementIntent: "Mobile",
    defenseIntent: "Counter",
    defenseFamily: "Mixed",
  },
};

/**
 * Plans authored before rounds stored a theme are recovered from their title.
 * Anything unrecognised falls through to freestyle, which constrains the least.
 */
export const LEGACY_COMBO_THEME_TITLES: Readonly<Record<string, GuidedComboTheme>> = {
  "Find your range": "straightShots",
  "Build the base": "leadHook",
  "Head movement": "slips",
  "Inside work": "uppercuts",
  "Body work": "bodyWork",
  "Block and return": "blocks",
  "Fast hands": "fastHands",
  Angles: "rolls",
  Power: "powerAccents",
  "Power round": "powerAccents",
  Freestyle: "freestyle",
  Championship: "freestyle",
};

export function comboThemeForLegacyTitle(title: string): GuidedComboTheme {
  return LEGACY_COMBO_THEME_TITLES[title] ?? "freestyle";
}

// ---------------------------------------------------------------------------
// Techniques
// ---------------------------------------------------------------------------

/** A library entry that can be inserted or dragged onto a beat. */
export type ComboTechnique =
  | { readonly kind: "punch"; readonly punch: PunchNumber }
  | { readonly kind: "defense"; readonly move: DefenseMove }
  | { readonly kind: "movement"; readonly move: FootworkMove };

export function comboTechniqueTransferId(technique: ComboTechnique): string {
  switch (technique.kind) {
    case "punch":
      return `punch-${technique.punch}`;
    case "defense":
      return `defense-${technique.move}`;
    case "movement":
      return `movement-${technique.move}`;
  }
}

export function comboTechniqueLabel(technique: ComboTechnique): string {
  switch (technique.kind) {
    case "punch":
      return `${technique.punch} · ${punchName(technique.punch)}`;
    case "defense":
      return DEFENSE_SPOKEN_LABELS[technique.move];
    case "movement":
      return FOOTWORK_CUE_LABELS[technique.move];
  }
}

/**
 * A drop payload is untrusted text, so an unparseable id is rejected rather
 * than coerced into some default technique the user never chose.
 */
export function parseComboTechniqueTransferId(value: string): ComboTechnique | null {
  if (value.startsWith("punch-")) {
    const raw = Number.parseInt(value.slice("punch-".length), 10);
    if (PUNCH_NUMBERS.includes(raw as PunchNumber)) {
      return { kind: "punch", punch: raw as PunchNumber };
    }
    return null;
  }
  if (value.startsWith("defense-")) {
    const raw = value.slice("defense-".length) as DefenseMove;
    return DEFENSE_MOVES.includes(raw) ? { kind: "defense", move: raw } : null;
  }
  if (value.startsWith("movement-")) {
    const raw = value.slice("movement-".length) as FootworkMove;
    return FOOTWORK_MOVES.includes(raw) ? { kind: "movement", move: raw } : null;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Transactions
// ---------------------------------------------------------------------------

/**
 * Why a remix was requested. The reason changes only the announcement, but it
 * has to travel with the transaction so a stance change is not announced as if
 * the user pressed Remix.
 */
export type ComboRemixReason = "transport" | "applyConstraints" | "stanceChange";

/**
 * Every mutation of the score, as data.
 *
 * Compose applies these; Rehearse and Ringside apply none. Modelling edits as a
 * closed union is what lets one score be proved to be the score all three
 * surfaces read: there is no second write path for a surface to diverge through.
 */
export type ComboTransaction =
  | { readonly kind: "selectBeat"; readonly beatId: ComboBeatId | null }
  | {
      readonly kind: "insertTechnique";
      readonly technique: ComboTechnique;
      readonly after: ComboBeatId | null;
    }
  | {
      readonly kind: "insertChunk";
      readonly beats: readonly ComboBeatDraft[];
      readonly after: ComboBeatId | null;
    }
  | { readonly kind: "setPunch"; readonly beatId: ComboBeatId; readonly punch: PunchNumber }
  | { readonly kind: "setPower"; readonly beatId: ComboBeatId; readonly power: PowerLevel }
  | { readonly kind: "setTarget"; readonly beatId: ComboBeatId; readonly target: PunchTarget }
  | { readonly kind: "setDefense"; readonly beatId: ComboBeatId; readonly move: DefenseMove }
  | { readonly kind: "setThreat"; readonly beatId: ComboBeatId; readonly threat: DefenseThreat }
  | {
      readonly kind: "setDefenseResolution";
      readonly beatId: ComboBeatId;
      readonly resolution: DefenseResolution;
    }
  | {
      readonly kind: "setMovement";
      readonly beatId: ComboBeatId;
      readonly movement: FootworkMove | null;
    }
  | {
      readonly kind: "setLevelTransition";
      readonly beatId: ComboBeatId;
      readonly levelTransition: LevelTransition | null;
    }
  | { readonly kind: "toggleLock"; readonly beatId: ComboBeatId }
  | { readonly kind: "duplicateBeat"; readonly beatId: ComboBeatId }
  | { readonly kind: "removeBeat"; readonly beatId: ComboBeatId }
  | { readonly kind: "moveBeat"; readonly beatId: ComboBeatId; readonly offset: -1 | 1 }
  | {
      readonly kind: "reorderBeat";
      readonly sourceId: ComboBeatId;
      readonly targetId: ComboBeatId;
    }
  | {
      readonly kind: "createPhraseBracket";
      readonly name: string;
      readonly beatIds: readonly ComboBeatId[];
      readonly repeatCount: number;
    }
  | { readonly kind: "removePhraseBracket"; readonly bracketId: PhraseBracketId }
  | {
      readonly kind: "setPhraseRepeatCount";
      readonly bracketId: PhraseBracketId;
      readonly repeatCount: number;
    }
  | { readonly kind: "addResponseBranch"; readonly beatId: ComboBeatId }
  | { readonly kind: "addResponseOption"; readonly beatId: ComboBeatId }
  | {
      readonly kind: "selectResponseOption";
      readonly beatId: ComboBeatId;
      readonly optionId: ResponseOptionId;
    }
  | {
      readonly kind: "remixUnlocked";
      readonly replacement: ComboScore;
      readonly reason: ComboRemixReason;
    }
  | { readonly kind: "setStance"; readonly stance: BoxingStance; readonly replacement: ComboScore }
  | {
      readonly kind: "replaceScore";
      readonly score: ComboScore;
      readonly reason: "newCombo" | "loadSaved";
    }
  | { readonly kind: "undo" }
  | { readonly kind: "redo" };

export type ComboTransactionRejection =
  | "noSelection"
  | "beatMissing"
  | "beatKindMismatch"
  | "noChange"
  | "phraseNotContiguous"
  | "branchLimitReached"
  | "nothingToUndo"
  | "nothingToRedo";

/**
 * Applied or rejected — never "applied but nothing happened". A no-op edit must
 * not push an undo entry, so the caller has to see the difference.
 */
export type ComboTransactionOutcome =
  | {
      readonly status: "applied";
      readonly session: ComboEditingSession;
      readonly announcement: string;
    }
  | { readonly status: "rejected"; readonly reason: ComboTransactionRejection };

/**
 * Compose's undo/redo state. Selection is deliberately outside the score: it is
 * a per-window cursor, and persisting it would make two Studio windows fight
 * over one saved favorite.
 */
export interface ComboEditingSession {
  readonly score: ComboScore;
  readonly selectedBeatId: ComboBeatId | null;
  readonly undoStack: readonly ComboScore[];
  readonly redoStack: readonly ComboScore[];
}

export function canUndo(session: ComboEditingSession): boolean {
  return session.undoStack.length > 0;
}

export function canRedo(session: ComboEditingSession): boolean {
  return session.redoStack.length > 0;
}

export function selectedBeat(session: ComboEditingSession): ComboBeat | null {
  if (session.selectedBeatId === null) return null;
  return comboBeatById(session.score, session.selectedBeatId);
}

// ---------------------------------------------------------------------------
// Remix
// ---------------------------------------------------------------------------

export interface ComboRemixResult {
  readonly beats: readonly ComboBeat[];
  readonly changedBeatCount: number;
  readonly lockedBeatCount: number;
  readonly beforeLabel: string;
  readonly afterLabel: string;
}

/**
 * Replaces only unlocked beats, cycling the replacement modulo its length so
 * the beat count never changes. Locked beats keep their identity *and* their
 * position, which is what lets brackets and branches carry through a remix
 * untouched — the whole reason lock exists.
 */
export function applyRemixToBeats(
  current: readonly ComboBeat[],
  replacement: readonly ComboBeat[],
): { readonly beats: readonly ComboBeat[]; readonly changedBeatCount: number } {
  if (replacement.length === 0) {
    return { beats: current, changedBeatCount: 0 };
  }
  let changed = 0;
  const beats = current.map((beat, index) => {
    if (beat.isLocked) return beat;
    const source = replacement[index % replacement.length];
    if (source === undefined) return beat;
    const rebuilt: ComboBeat =
      source.kind === "punch"
        ? { ...source, id: beat.id, isLocked: false }
        : { ...source, id: beat.id, isLocked: false };
    if (!isSameBeatContent(beat, rebuilt)) changed += 1;
    return rebuilt;
  });
  return { beats, changedBeatCount: changed };
}

/** Identity and lock state are excluded: a remix that only preserved them changed nothing. */
export function isSameBeatContent(left: ComboBeat, right: ComboBeat): boolean {
  if (left.kind !== right.kind) return false;
  if (left.kind === "punch" && right.kind === "punch") {
    return (
      left.punch === right.punch &&
      left.power === right.power &&
      left.target === right.target &&
      left.range === right.range &&
      left.movement === right.movement &&
      left.levelTransition === right.levelTransition
    );
  }
  if (left.kind === "defense" && right.kind === "defense") {
    return (
      left.move === right.move &&
      left.range === right.range &&
      left.movement === right.movement &&
      left.levelTransition === right.levelTransition &&
      left.resolution === right.resolution
    );
  }
  return false;
}

// ---------------------------------------------------------------------------
// Rehearsal
// ---------------------------------------------------------------------------

export type ComboRehearsalMode = "read" | "follow" | "recall" | "react";

export const COMBO_REHEARSAL_MODES: readonly ComboRehearsalMode[] = [
  "read",
  "follow",
  "recall",
  "react",
];

export const REHEARSAL_MODE_LABELS: Readonly<Record<ComboRehearsalMode, string>> = {
  read: "Read",
  follow: "Follow",
  recall: "Recall",
  react: "React",
};

export const REHEARSAL_MODE_GUIDANCE: Readonly<Record<ComboRehearsalMode, string>> = {
  read: "See the complete score and move at your own pace.",
  follow: "Keep the active beat in the spotlight as you advance.",
  recall: "Recall the active beat, then reveal it when ready.",
  react: "Read the planned threat first, then reveal your response.",
};

/** Not user-configurable: rehearsal pacing is a rhythm cue, not a prescription. */
export const REHEARSAL_AUTO_ADVANCE_INTERVAL: Milliseconds = 1_600;

/** What the surface should show. The payloads are what makes React mode legible. */
export type ComboRehearsalVisibility =
  | { readonly kind: "completeScore" }
  | { readonly kind: "spotlight" }
  | { readonly kind: "prompt" }
  | { readonly kind: "revealed" }
  | { readonly kind: "threat"; readonly threat: DefenseThreat }
  | {
      readonly kind: "plannedResponse";
      readonly threat: DefenseThreat;
      readonly option: ComboResponseOption | null;
    };

/**
 * Rehearse reads the same score Compose edits — it never clones combination
 * state — and records which revision it last synced from so a stale render is
 * detectable rather than silent.
 */
export interface ComboRehearsalSession {
  readonly score: ComboScore;
  readonly syncedRevision: ComboScoreRevision;
  readonly mode: ComboRehearsalMode;
  readonly currentBeatIndex: number;
  readonly isPlaying: boolean;
  readonly isRevealed: boolean;
}

/** Read and Follow show the beat outright; Recall and React must be earned. */
export function revealedByDefault(mode: ComboRehearsalMode): boolean {
  return mode === "read" || mode === "follow";
}

export function currentRehearsalBeat(session: ComboRehearsalSession): ComboBeat | null {
  return session.score.beats[session.currentBeatIndex] ?? null;
}

export function rehearsalProgressLabel(session: ComboRehearsalSession): string {
  if (session.score.beats.length === 0) return "No beats";
  return `Beat ${session.currentBeatIndex + 1} of ${session.score.beats.length}`;
}

export function rehearsalVisibility(session: ComboRehearsalSession): ComboRehearsalVisibility {
  switch (session.mode) {
    case "read":
      return { kind: "completeScore" };
    case "follow":
      return { kind: "spotlight" };
    case "recall":
      return session.isRevealed ? { kind: "revealed" } : { kind: "prompt" };
    case "react": {
      const beat = currentRehearsalBeat(session);
      if (beat === null || beat.kind !== "defense") {
        // React only differs from Recall on a defensive beat; there is no threat
        // to read before a punch you are throwing.
        return session.isRevealed ? { kind: "revealed" } : { kind: "prompt" };
      }
      const branch = responseBranchFor(session.score, beat.id);
      const threat = branch?.threat ?? DEFENSE_IMPLIED_THREATS[beat.move];
      if (!session.isRevealed) return { kind: "threat", threat };
      return {
        kind: "plannedResponse",
        threat,
        option: branch === null ? null : selectedResponseOption(branch),
      };
    }
  }
}

/** The score obscures exactly the beat the athlete is being asked to recall. */
export function isBeatObscured(
  visibility: ComboRehearsalVisibility,
  index: number,
  currentIndex: number,
): boolean {
  if (index !== currentIndex) return false;
  return visibility.kind === "prompt" || visibility.kind === "threat";
}

// ---------------------------------------------------------------------------
// Ringside
// ---------------------------------------------------------------------------

export type RingsideDistanceMode = "Hand" | "Stand" | "Room";

export const RINGSIDE_DISTANCE_MODES: readonly RingsideDistanceMode[] = ["Hand", "Stand", "Room"];

/** Stand is the default: close enough to read, far enough to actually work. */
export const RINGSIDE_DEFAULT_DISTANCE_MODE: RingsideDistanceMode = "Stand";

export const RINGSIDE_DISTANCE_GUIDANCE: Readonly<Record<RingsideDistanceMode, string>> = {
  Hand: "Complete score and detailed controls",
  Stand: "Large active phrase with nearby transport",
  Room: "Maximum clock and cue legibility across the room",
};

/**
 * The live consumer. It holds no score of its own — only the id and revision it
 * is displaying — because Ringside must never be able to edit or fork the score
 * the athlete is mid-round on.
 */
export interface RingsideSpotlightState {
  readonly scoreId: ComboId;
  readonly syncedRevision: ComboScoreRevision;
  readonly activeBeatId: ComboBeatId | null;
  readonly distanceMode: RingsideDistanceMode;
  /**
   * A locked session passes no selection through, which is what makes the
   * read-only score non-interactive rather than merely visually inert.
   */
  readonly controlsLocked: boolean;
}

export function ringsideTitle(beat: ComboBeat | null, score: ComboScore): string {
  if (beat === null || beat.kind !== "defense") return RINGSIDE_COPY.phraseTitle;
  const branch = responseBranchFor(score, beat.id);
  const threat = branch?.threat ?? DEFENSE_IMPLIED_THREATS[beat.move];
  return `Answer ${DEFENSE_THREAT_SPOKEN_LABELS[threat]}`;
}

export function ringsideCurrentLabel(beat: ComboBeat): string {
  return beat.kind === "punch" ? "CURRENT · PUNCH" : "CURRENT · DEFENSE";
}

/** The detail line every dense surface shares, joined by " · ". */
export function beatDetailLine(beat: ComboBeat, threat: DefenseThreat): string {
  if (beat.kind === "punch") {
    const parts = [`${beat.target} · P${beat.power} ${POWER_LABELS[beat.power].toLowerCase()}`];
    if (beat.levelTransition !== null) parts.push(beat.levelTransition);
    if (beat.movement !== null) parts.push(FOOTWORK_CUE_LABELS[beat.movement]);
    return parts.join(" · ");
  }
  return `Against ${threat} · ${DEFENSE_RESOLUTION_LABELS[beat.resolution]}`;
}

// ---------------------------------------------------------------------------
// Tactical Score presentation
// ---------------------------------------------------------------------------

export type TacticalScorePresentation = "full" | "fit" | "ordered" | "spotlight";

export const TACTICAL_SCORE_PRESENTATIONS: readonly TacticalScorePresentation[] = [
  "full",
  "fit",
  "ordered",
  "spotlight",
];

export type DynamicTypeSize = "standard" | "accessibility";

/**
 * Fallback order, in the order it must be evaluated:
 *   1. accessibility text size wins over any requested presentation;
 *   2. `.ordered` forces the list even at standard sizes;
 *   3. per-beat actions keep their declaration order in both paths, so the
 *      assistive-technology rotor sequence does not change with layout.
 */
export function resolveTacticalScorePresentation(
  requested: TacticalScorePresentation,
  typeSize: DynamicTypeSize,
): TacticalScorePresentation {
  return typeSize === "accessibility" ? "ordered" : requested;
}

export type ComboStudioLayout = "narrow" | "half" | "full";

export type HorizontalSizeClass = "compact" | "regular";

export function resolveComboStudioLayout(input: {
  readonly width: number;
  readonly horizontalSizeClass: HorizontalSizeClass;
  readonly typeSize: DynamicTypeSize;
}): ComboStudioLayout {
  if (input.typeSize === "accessibility") return "narrow";
  if (input.horizontalSizeClass === "compact") return "narrow";
  if (input.width < 680) return "narrow";
  return input.width < 1000 ? "half" : "full";
}

export type ComboEditLayer = "Sequence" | "Power" | "Phrase";

export const COMBO_EDIT_LAYERS: readonly ComboEditLayer[] = ["Sequence", "Power", "Phrase"];

export type ComboStudioWorkspace = "compose" | "rehearse";

/**
 * Fixed canvas geometry. The lane rows are the whole grammar of the score:
 * height on the page means height on the body, so these numbers are a contract
 * with the drawing, not styling.
 */
export const TACTICAL_SCORE_GEOMETRY = {
  canvasHeight: 252,
  /** Above six beats the nodes shrink rather than crowd or clip. */
  compactBeatThreshold: 6,
  laneLabels: [
    { text: "THREAT", x: 27, y: 17 },
    { text: "HEAD", x: 21, y: 60 },
    { text: "DEFENSE", x: 28, y: 112 },
    { text: "BODY", x: 21, y: 162 },
    { text: "FOOTWORK", x: 31, y: 207 },
    { text: "PHRASE", x: 27, y: 236 },
  ],
  laneRuleYs: [39, 86, 138, 188, 221],
  ruleLeadingInset: 62,
  ruleTrailingInset: 18,
  /** Nodes never borrow the label rail or the trailing lock-badge gutter. */
  leadingNodeInset: 96,
  trailingNodeInset: 38,
  headNodeY: 60,
  defenseNodeY: 112,
  bodyNodeY: 162,
  responseBracketDrop: 40,
  responseBracketBottomInset: 9,
  phraseRailY: 241,
  phraseLabelY: 229,
} as const;

export interface TacticalScoreNodePosition {
  readonly beatId: ComboBeatId;
  readonly x: number;
  readonly y: number;
}

export function tacticalScoreNodePositions(
  beats: readonly ComboBeat[],
  width: number,
): readonly TacticalScoreNodePosition[] {
  const left = TACTICAL_SCORE_GEOMETRY.leadingNodeInset;
  const right = TACTICAL_SCORE_GEOMETRY.trailingNodeInset;
  const available = Math.max(1, width - left - right);
  const step = beats.length > 1 ? available / (beats.length - 1) : 0;
  return beats.map((beat, index) => ({
    beatId: beat.id,
    x: left + index * step,
    y:
      beat.kind === "defense"
        ? TACTICAL_SCORE_GEOMETRY.defenseNodeY
        : beat.target === "Body"
          ? TACTICAL_SCORE_GEOMETRY.bodyNodeY
          : TACTICAL_SCORE_GEOMETRY.headNodeY,
  }));
}

export function isCompactScore(beats: readonly ComboBeat[]): boolean {
  return beats.length > TACTICAL_SCORE_GEOMETRY.compactBeatThreshold;
}

/** Row id prefixes, one per surface, so a rotor never conflates two scores. */
export const ORDERED_SCORE_ROW_PREFIXES = {
  tacticalScore: "tacticalScore.beat",
  comboStudio: "ipad.comboStudio.beat",
  rehearsal: "ipad.comboStudio.rehearsal.beat",
  live: "ipad.live.combo.beat",
} as const;

export type OrderedScoreSurface = keyof typeof ORDERED_SCORE_ROW_PREFIXES;

export function orderedScoreRowId(surface: OrderedScoreSurface, index: number): string {
  return `${ORDERED_SCORE_ROW_PREFIXES[surface]}.${index}`;
}

/**
 * Named per-beat actions in their fixed declaration order. The order is
 * identical in the spatial and ordered presentations, so an assistive-technology
 * user gets the same rotor sequence no matter which layout is on screen.
 */
export const COMBO_BEAT_ACTION_ORDER = [
  "lockToggle",
  "duplicate",
  "earlier",
  "later",
  "remove",
] as const;

export type ComboBeatAction = (typeof COMBO_BEAT_ACTION_ORDER)[number];

export const COMBO_BEAT_ACTION_LABELS = {
  lock: "Lock beat",
  unlock: "Unlock beat",
  duplicate: "Duplicate beat",
  earlier: "Move beat earlier",
  later: "Move beat later",
  remove: "Remove beat",
} as const;

// ---------------------------------------------------------------------------
// Accessibility copy
// ---------------------------------------------------------------------------

/** The container label is always the same sentence plus the spoken call. */
export function tacticalScoreAccessibilityLabel(score: ComboScore): string {
  return `Tactical score. ${comboVoicePrompt(score)}`;
}

export function beatAccessibilityLabel(beat: ComboBeat, threat: DefenseThreat): string {
  if (beat.kind === "punch") {
    const parts = [
      `Punch ${beat.punch}, ${punchName(beat.punch)}, ${beat.target}, ${POWER_LABELS[beat.power]} power`,
    ];
    if (beat.levelTransition !== null) parts.push(beat.levelTransition);
    if (beat.movement !== null) parts.push(FOOTWORK_CUE_LABELS[beat.movement]);
    return parts.join(", ");
  }
  return `${beat.move}, against ${threat}`;
}

export const DEFENSE_RESOLUTION_HINTS: Readonly<Record<DefenseResolution, string>> = {
  counter: "Followed by a counter",
  exit: "Followed by an exit",
  returnToStance: "Return to stance",
};

/** A defensive beat with no stored resolution still describes defensive movement. */
export const DEFENSE_MOVEMENT_HINT = "Defensive movement";

/** State is spoken, never conveyed by colour or size alone. */
export function beatAccessibilityValue(state: {
  readonly isSelected: boolean;
  readonly isActive: boolean;
  readonly isLocked: boolean;
}): string {
  const parts: string[] = [];
  if (state.isSelected) parts.push("Selected");
  if (state.isActive) parts.push("Active");
  parts.push(state.isLocked ? "Locked" : "Unlocked");
  return parts.join(", ");
}

// ---------------------------------------------------------------------------
// Copy
// ---------------------------------------------------------------------------

export const COMBO_LAB_COPY = {
  generateTitle: "Combo Lab",
  savedTitle: "Saved combos",
  generateEyebrow: "TACTICAL CALLS",
  savedEyebrow: "YOUR CORNER",
  generateHeadline: "Call the next exchange",
  savedHeadline: "Saved combinations",
  generateSubtext: "A technically constrained phrase for shadowboxing or the bag.",
  savedEmptyLead: "Combinations you keep will be available in guided workouts.",
  savedEmptyTitle: "Nothing saved yet",
  savedEmptyDetail:
    "Keep a combination in the Lab and it will appear here—and in Saved or Mix workout calls.",
  savedEmptyAction: "Return to the Lab",
  hear: "Hear",
  save: "Save",
  saved: "Saved",
  another: "Another",
  anotherHint: "Skips this combination and creates another",
  tuneLabel: "Tune",
  footer: "Designed for solo shadow and bag work. Technique comes before power.",
} as const;

export function savedComboLead(count: number): string {
  return `${count} ready for saved-only calls or a mix with generated work.`;
}

export const COMBO_SHAPE_SHEET_COPY = {
  title: "Shape the combo",
  apply: "Apply and make a combo",
  introTitle: "One pattern, three decisions",
  introDetail:
    "Choose the shape first. Fighting Form keeps range, balance, and defensive responses coherent.",
  fieldSection: "Target × movement",
  fieldHint: "Tap one field",
  fieldGridLabel: "Target and movement field",
  defenseSection: "Defense",
  familySection: "Technique family",
  powerSection: "Power and finish",
} as const;

export const COMBO_TECHNIQUES_SHEET_COPY = {
  title: "Available techniques",
  apply: "Use these techniques",
  punchSection: "Punch vocabulary",
  punchHint: "Keep at least one",
  headMovementSection: "Head movement",
  headMovementHint: "Against punches to the head",
  slipsTitle: "Slips",
  slipsDetail: "Straight punches",
  rollsTitle: "Rolls",
  rollsDetail: "Hooks to the head",
  blocksSection: "Blocks and catches",
  blocksHint: "Every defense names its threat",
} as const;

export const COMBO_FLOW_COPY = {
  safeguardChip: "Safeguards stay on",
  exploratoryCaption: "Broader valid patterns, with range and balance constraints intact.",
} as const;

/** Pattern-summary fragments, joined by " · ". */
export const MOVEMENT_INTENT_SUMMARY: Readonly<Record<ComboMovementIntent, string>> = {
  Set: "Set and reset",
  Angle: "Angle out",
  Mobile: "Enter, work, exit",
};

export const MOVEMENT_INTENT_EXPLANATIONS: Readonly<Record<ComboMovementIntent, string>> = {
  Set: "Work from a balanced range and restore your stance before repeating.",
  Angle: "Finish with one pivot or step-off, then square up to the bag again.",
  Mobile:
    "Begin just outside range, enter once with the lead foot then rear, work, exit, and reset before the next repetition.",
};

export const POWER_FINISH_EXPLANATIONS: Readonly<Record<ComboPowerFinish, string>> = {
  Measured: "P1 and P2 rhythm without a required power shot.",
  "One accent": "One suitable punch carries P3 intent.",
  "Power finish": "The final suitable punch is P3, followed by a reset or exit.",
};

export const DEFENSE_INTENT_SUMMARY_NONE = "Offense only";

export function defenseIntentSummary(
  intent: ComboDefenseIntent,
  family: ComboDefenseFamily,
): string {
  if (intent === "None") return DEFENSE_INTENT_SUMMARY_NONE;
  const verb = intent === "Counter" ? "Counter" : "Defend";
  return `${verb} with ${family.toLowerCase()}`;
}

export const COMBO_STUDIO_COPY = {
  title: "Combo Studio",
  eyebrow: "COMBO SYSTEM",
  composeHeadline: "Compose the exchange",
  rehearseHeadline: "Rehearse the score",
  workspacePicker: "Combo workspace",
  compose: "Compose",
  rehearse: "Rehearse",
  scoreTitle: "TACTICAL SCORE",
  spokenScorePrefix: "Spoken score.",
  editLayerPicker: "Edit layer",
  constraintsTitle: "GENERATOR CONSTRAINTS",
  constraintsDetail: "Tune the next remix without prescribing workout time or repetitions.",
  applyWithRemix: "Apply with remix",
  powerLayerHeader: "POWER INTENT",
  phraseLayerHeader: "PHRASE BRACKETS",
  phraseLayerDetail:
    "Choose adjacent beats, name the chunk, then set how often its relative structure repeats.",
  phraseNameField: "Phrase name",
  createPhraseBracket: "Create phrase bracket",
  inspectorEmptyTitle: "Select a beat",
  inspectorEmptyDetail:
    "Tap a score node to edit its technique, target, power, threat, response, movement, or lock state.",
  inspectorSheetTitle: "Edit beat",
  responsesHeader: "PLANNED RESPONSES",
  addAlternate: "Add alternate",
  remixUnlocked: "Remix unlocked",
  remixHint: "Changes only unlocked beats and creates one undoable edit",
  removeBeatTitle: "Remove this beat?",
  removeBeatConfirm: "Remove beat",
  removeBeatCancel: "Keep beat",
  removeBeatMessage:
    "The beat and any response branch attached to it will be removed. You can undo this edit.",
  techniqueInsertHint: "Inserts after the selected beat. You can also drag it onto a beat.",
  movementInsertHint: "Applies this footwork cue to the selected beat.",
  rehearsalModeHeader: "REHEARSAL MODE",
} as const;

export function noBranchYetCopy(threat: DefenseThreat): string {
  return `No branch yet. React mode will present ${threat} and the defense itself.`;
}

export const REHEARSAL_PROMPT_COPY = {
  completeScoreTitle: "Read the complete score",
  revealedTitle: "Revealed",
  recallDetail:
    "Say or move the beat from memory, then choose Reveal. Nothing is detected or scored.",
  threatDetail: "Choose Reveal when you are ready to see the response you planned.",
  plannedResponseFallback:
    "Use the defensive beat shown in the score, then return safely to stance.",
  obscuredRowTitle: "Recall this beat",
  obscuredRowDetail: "Activate Reveal when ready",
} as const;

export function rehearsalSpotlightTitle(index: number, total: number): string {
  return `Follow beat ${index} of ${total}`;
}

export function rehearsalRecallTitle(index: number, total: number): string {
  return `Recall beat ${index} of ${total}`;
}

export function rehearsalThreatTitle(threat: DefenseThreat): string {
  // Capitalised because it heads the card rather than sitting inside a sentence.
  return `Threat: ${threat.charAt(0).toUpperCase()}${threat.slice(1)}`;
}

export function rehearsalPlannedResponseTitle(threat: DefenseThreat): string {
  return `Against ${threat}`;
}

export const RINGSIDE_COPY = {
  eyebrow: "RINGSIDE SPOTLIGHT",
  phraseTitle: "Stay with the phrase",
  distancePicker: "Viewing distance",
} as const;

/**
 * The whole product boundary in one paragraph: this app coaches solo work. It
 * must appear wherever generated defense does.
 */
export const COMBO_SAFETY_COPY = {
  labFooter: "Designed for solo shadow and bag work. Technique comes before power.",
  full:
    "Designed for solo shadow and bag work. Practice partner drills or sparring only with a qualified coach and proper protective equipment. Technique comes before power. Stop for pain or possible head injury and seek appropriate care.",
  techniquesSheet:
    "Solo shadow and bag rehearsal only. Practice partner drills or sparring with a qualified coach and proper protective equipment.",
} as const;

// ---------------------------------------------------------------------------
// Announcements
// ---------------------------------------------------------------------------

/**
 * Every mutating action funnels through one announcement so the same edit is
 * never described two different ways — and the visual toast is hidden from
 * assistive technology precisely because this string is already spoken.
 */
export const COMBO_ANNOUNCEMENTS = {
  undo: "Undid combo edit.",
  redo: "Redid combo edit.",
  duplicated: "Duplicated beat.",
  movedEarlier: "Moved beat earlier.",
  movedLater: "Moved beat later.",
  removedBeat: "Removed beat. Undo is available.",
  addedBranch: "Added a planned response branch.",
  phraseNotContiguous: "Choose at least two adjacent beats for a phrase bracket.",
  savedUpdated: "Saved combo updated.",
  saved: "Combo saved.",
  removedFromSaved: "Combo removed from saved.",
  rehearsalComplete: "Rehearsal complete.",
  movementRemoved: "Movement removed.",
  levelCueRemoved: "Level cue removed.",
} as const;

export const COMBO_ANNOUNCEMENT_PREFIXES = {
  newCombo: "New combo",
  updatedCombo: "Updated combo",
  loadedSaved: "Loaded saved combo",
} as const;

export type ComboAnnouncementPrefix =
  (typeof COMBO_ANNOUNCEMENT_PREFIXES)[keyof typeof COMBO_ANNOUNCEMENT_PREFIXES];

export function generatedComboAnnouncement(
  prefix: ComboAnnouncementPrefix,
  score: ComboScore,
): string {
  return `${prefix}. ${comboVoicePrompt(score)}`;
}

export function lockAnnouncement(beatNumber: number, isLocked: boolean): string {
  return `${isLocked ? "Locked" : "Unlocked"} beat ${beatNumber}.`;
}

export function selectedBeatAnnouncement(index: number, total: number): string {
  return `Selected beat ${index} of ${total}.`;
}

export function phraseCreatedAnnouncement(name: string): string {
  return `Created ${name} phrase bracket.`;
}

export function phraseRemovedAnnouncement(name: string): string {
  return `Removed ${name} phrase bracket.`;
}

export function phraseRepeatAnnouncement(name: string, repeatCount: number): string {
  return `${name} now repeats ${repeatCount} times.`;
}

export function insertedTechniqueAnnouncement(label: string): string {
  return `Inserted ${label}.`;
}

export function updatedBeatWithTechniqueAnnouncement(label: string): string {
  return `Updated the selected beat with ${label}.`;
}

export function insertedChunkAnnouncement(beatCount: number): string {
  return `Inserted ${beatCount}-beat saved chunk.`;
}

export function rehearsalModeAnnouncement(mode: ComboRehearsalMode): string {
  return `${REHEARSAL_MODE_LABELS[mode]} mode. ${REHEARSAL_MODE_GUIDANCE[mode]}`;
}

export function rehearsalStartAnnouncement(mode: ComboRehearsalMode): string {
  return `${REHEARSAL_MODE_LABELS[mode]} rehearsal. ${REHEARSAL_MODE_GUIDANCE[mode]}`;
}

export function responseOptionSelectedAnnouncement(name: string): string {
  return `Selected ${name}.`;
}

export const INSPECTOR_ANNOUNCEMENTS = {
  punch: (name: string) => `Changed punch to ${name}.`,
  target: (target: PunchTarget) => `Target changed to ${target}.`,
  power: (power: PowerLevel) => `Power intent changed to ${POWER_LABELS[power]}.`,
  defense: (move: DefenseMove) => `Defense changed to ${DEFENSE_SPOKEN_LABELS[move]}.`,
  threat: (threat: DefenseThreat) => `Threat changed to ${threat}.`,
  resolution: (resolution: DefenseResolution) =>
    `Resolution changed to ${DEFENSE_RESOLUTION_LABELS[resolution]}.`,
  movement: (movement: FootworkMove) => `Movement changed to ${FOOTWORK_CUE_LABELS[movement]}.`,
  levelTransition: (transition: LevelTransition) => `Level cue changed to ${transition}.`,
} as const;

/**
 * A remix that changed nothing still has to say so, and it has to distinguish
 * "everything is locked" from "the generator produced the same beats" — those
 * are different problems for the user to act on.
 */
export function remixAnnouncement(result: ComboRemixResult, reason: ComboRemixReason): string {
  const body =
    result.changedBeatCount === 0
      ? result.lockedBeatCount > 0
        ? "Nothing remixed. All available beats are locked or unchanged."
        : "Nothing changed in this remix."
      : `Remixed ${result.changedBeatCount} unlocked ${
          result.changedBeatCount === 1 ? "beat" : "beats"
        }. Kept ${result.lockedBeatCount} locked. Before: ${result.beforeLabel}. After: ${
          result.afterLabel
        }.`;
  return reason === "stanceChange" ? `Stance changed. ${body}` : body;
}

/** Only a non-contiguous phrase selection speaks; the rest are silent refusals. */
export function rejectionAnnouncement(reason: ComboTransactionRejection): string | null {
  return reason === "phraseNotContiguous" ? COMBO_ANNOUNCEMENTS.phraseNotContiguous : null;
}

// ---------------------------------------------------------------------------
// Storage keys
// ---------------------------------------------------------------------------

/**
 * The Mac target writes its own parameter record so a desktop session cannot
 * silently rewrite what the phone generates next.
 */
export const COMBO_STORAGE_KEYS = {
  parameters: "fightingform.combo-parameters",
  macParameters: "fightingform.mac.combo-parameters",
  favorites: "fightingform.favorite-combos",
  ringsideDistance: "ringside.viewingDistance",
} as const;

/** Combo Studio restores per scene, so two windows keep independent state. */
export const COMBO_STUDIO_SCENE_KEYS = {
  mode: "mode",
  layer: "layer",
  presentation: "presentation",
  selectedBeat: "selectedBeat",
} as const;

export const COMBO_STUDIO_SCENE_DEFAULTS = {
  mode: "compose",
  layer: "Sequence",
  presentation: "full",
  selectedBeat: "",
} as const;
